import { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  List,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import ValidationItem from "../components/ValidationItem";

export default function Register() {
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirm: "",
    full_name: "",
  });

  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Validation State
  const [checks, setChecks] = useState({
    length: false,
    number: false,
    special: false,
    match: false,
  });

  // Real-time validation logic
  useEffect(() => {
    setChecks({
      length: form.password.length >= 8,
      number: /[0-9]/.test(form.password),
      special: /[!@#$%^&*]/.test(form.password),
      match: form.password !== "" && form.password === form.confirm,
    });
  }, [form.password, form.confirm]);

  const isFormValid =
    Object.values(checks).every(Boolean) && form.email && form.full_name;

  const change = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setMsg({ type: "", text: "" });
    setLoading(true);

    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          // Collapse stray leading/trailing/double spaces so this name
          // matches exactly what admins later type into bulk upload sheets.
          full_name: form.full_name.trim().replace(/\s+/g, " "),
        },
      },
    });

    if (error) {
      setMsg({ type: "error", text: error.message });
      setLoading(false);
    } else {
      setMsg({
        type: "success",
        text: "Account created! Please check your email for a confirmation link.",
      });
      setLoading(false);
    }
  };

  // -------------------- Render -------------------- //

  return (
    <Container maxWidth="xs" sx={{ py: 6, position: "relative" }}>
      {/* BACK BUTTON */}
      <Box sx={{ position: "absolute", top: 16, left: 16 }}>
        <Tooltip title="Back to Home">
          <IconButton onClick={() => navigate("/")} color="primary">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Register
      </Typography>

      {msg.text && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Box component="form" onSubmit={submit} noValidate>
        <TextField
          label="Full Name"
          name="full_name"
          fullWidth
          margin="normal"
          value={form.full_name}
          onChange={change}
          required
        />

        <TextField
          label="Email"
          name="email"
          type="email"
          fullWidth
          margin="normal"
          value={form.email}
          onChange={change}
          required
        />

        <TextField
          label="Password"
          type="password"
          name="password"
          fullWidth
          margin="normal"
          value={form.password}
          onChange={change}
          required
        />

        {/* Real-time Validation Checklist */}
        <List sx={{ mb: 1 }}>
          <ValidationItem
            fulfilled={checks.length}
            text="At least 8 characters"
          />
          <ValidationItem fulfilled={checks.number} text="Contains a number" />
          <ValidationItem
            fulfilled={checks.special}
            text="Contains a special character (!@#$)"
          />
        </List>

        <TextField
          label="Confirm Password"
          type="password"
          name="confirm"
          fullWidth
          margin="normal"
          value={form.confirm}
          onChange={change}
          onPaste={(e) => e.preventDefault()} // Enforce typing for confirmation
          required
        />
        <ValidationItem fulfilled={checks.match} text="Passwords match" />

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          type="submit"
          disabled={loading || !isFormValid}
        >
          {loading ? "Creating Account..." : "Sign Up"}
        </Button>

        <Typography sx={{ mt: 2 }} textAlign="center">
          Already have an account? <Link to="/login">Login</Link>
        </Typography>
      </Box>
    </Container>
  );
}
